import { z } from 'zod';
import { cardListRefSchema } from './card-detail';
import { cardSchema } from './card';
import { ApiError } from './errors';
import { ulidSchema } from './id';
import { listSchema } from './list';

/**
 * Archive and recovery — the contract layer for block **B22**.
 *
 * `BE-22.1` (`FR-BOARD-028` archive a card, `FR-EXP-011` permanently delete
 * an archived card — two routes, `POST /cards/:id/archive` and
 * `DELETE /cards/:id`, on `apps/server/src/routes/card-archive.ts`),
 * `BE-22.1`'s restore half (`FR-EXP-010`, `FR-CARD-028`, `FR-TMPL-031` —
 * `POST /cards/:id/restore`, same file) and `BE-22.2` (`FR-EXP-010`,
 * `FR-BOARD-029` — the read, `GET /boards/:id/archive`, on
 * `apps/server/src/routes/board-archive.ts`).
 *
 * ---------------------------------------------------------------------------
 * ONE CONTRACT FILE, TWO ROUTE FILES — A SHAPE THIS PACKAGE HAS NOT HAD YET
 * ---------------------------------------------------------------------------
 * `./search` is one route file and one contract file; `./cross-board-move` is
 * two routes sharing one contract file because they share their whole report.
 * This is the third combination: **two route files**, `card-archive.ts` (three
 * mutations) and `board-archive.ts` (one read), sharing **one** contract file,
 * because the read's response shape (`archivedCardSchema`, `archivedListSchema`)
 * and the mutations' request shape (`cardRestoreBodySchema`) describe the same
 * lifecycle from its two ends — a client cannot page the archive without the
 * view this file declares, and cannot restore a row from that page without the
 * body this file declares next to it. Splitting them would be `./board-filter`
 * and `./board-contents`'s split reapplied to a pair of routes that, unlike
 * those two, are not independently useful: the archive listing exists so a
 * member can find the row to restore or delete, and the restore body exists to
 * act on a row the listing produced.
 *
 * ---------------------------------------------------------------------------
 * ⚠ THIS FILE DECLARES NO NOUN OF `BE-02.1`'s EIGHT, AND THE COUNT IS THE TRAP
 * `./index`'s HEADER HAS ALREADY SPRUNG FIVE TIMES
 * ---------------------------------------------------------------------------
 * Like `./board-filter` (a parameter's contract), `./search` (a route's two
 * halves) and `./cross-board-move` (two routes' request and response
 * contracts), this is **a route's request and response contract and nothing
 * more**. "Archive and recovery" is not one of `BE-02.1`'s eight nouns; every
 * view below either *is* `cardSchema`/`listSchema` extended on a copy or nests
 * `cardListRefSchema`, all three imported rather than restated. Seven of the
 * eight have a file; **membership** is still the one that does not, and
 * `ls packages/shared/src` is still the instruction.
 *
 * ---------------------------------------------------------------------------
 * IT DECLARES NO COLUMN CONSTANT, ON `./card`'s RULE WITH ITS SECOND BRANCH
 * NEVER TAKEN — `./cross-board-move`'s precedent exactly
 * ---------------------------------------------------------------------------
 * `./card`'s standing rule: import the constant that describes **your** column
 * if one exists, declare it if none does, and never share one across two
 * columns. B22 introduces no column — it reads `cards.archived_at`,
 * `cards.deleted_at`, `lists.archived_at` and `lists.deleted_at`, all four
 * created by earlier blocks, and writes only `cards.archived_at`,
 * `cards.deleted_at` and `cards.list_id`/`cards.position` on the
 * choose-a-destination branch, again all pre-existing columns. What this file
 * *does* declare are two pagination bounds (`ARCHIVE_PAGE_SIZE_DEFAULT`,
 * `ARCHIVE_PAGE_SIZE_MAX`) — not column widths, `./comment`'s
 * `COMMENT_PAGE_SIZE_*` precedent exactly, a request-shape bound rather than a
 * bound over somebody else's data.
 *
 * ---------------------------------------------------------------------------
 * ⚠⚠ `restorableInPlace` IS `OQ-3`'s ANSWER ON THE WIRE, AND IT EXISTS SO
 * `FE-22.2` NEVER HAS TO GUESS
 * ---------------------------------------------------------------------------
 * D1 of the block contract settles `OQ-3`: the server restores a card in place
 * when its own list is live and unarchived, and otherwise refuses with a
 * machine-readable `409` naming the required destination — there is no
 * server-chosen fallback list. `restorableInPlace` is that same predicate,
 * computed once by the same code path and served on every row of the `cards`
 * archive page, so a client rendering the list can decide **before** the
 * member clicks Restore whether to send a bare `{}` or to open
 * `FR-EXP-010` AC3's destination picker — never by calling `POST
 * /cards/:id/restore` speculatively and branching on the status code. A
 * client that guessed instead (`list.archived === false` therefore
 * restorable) would be **right today and wrong the day this listing's `WHERE`
 * changes**, because `list.archived` alone does not encode the other half of
 * D1's predicate — see the field's own docstring for the one case that
 * separates them, which this listing's own `WHERE` currently makes
 * unreachable but the field does not assume that forever.
 *
 * ---------------------------------------------------------------------------
 * ⚠⚠ `cardCount` ON `archivedListSchema` IS INHERITED FROM `listSchema`,
 * **NOT REDECLARED** — AND THAT IS THE POINT, NOT AN OMISSION
 * ---------------------------------------------------------------------------
 * The design note behind this file asks for a `cardCount` on the archived-list
 * row that counts "open and archived cards alike, excluding soft-deleted
 * ones" — `FR-EXP-011` AC2's confirmation must name the number of cards
 * `DELETE /lists/:id` will *also* delete permanently. Read `listSchema`'s own
 * `cardCount` docstring in `./list` before adding a field here: it is already
 * exactly that number, word for word — "`lists`' cards with `deleted_at IS
 * NULL`, **archived and open alike**" — because B11 wrote it for
 * `FR-BOARD-016`'s identical confirmation one level up, and `FR-EXP-011` AC2
 * asks for the same fact `FR-BOARD-016` already asks for. So
 * `archivedListSchema` below extends `listSchema` with `archivedAt` alone;
 * `cardCount` rides through unchanged, and the two confirmations are
 * **provably** reading one value rather than two declarations a future edit
 * could let drift. Redeclaring it — `listSchema.extend({ archivedAt,
 * cardCount: z.number()… })` — would shadow the inherited field with a second
 * one of the same name and the same type, which typechecks, changes nothing
 * observable, and is the exact kind of restatement `./card-detail`'s and
 * `./cross-board-move`'s headers both warn against: *extend on a copy for
 * what one route needs; never restate a field that already means what you
 * need.* This is also why the field is **not** named `openCardCount`: that is
 * `./board-contents`'s field, counts a **different** population (open only),
 * and reusing its name here for a number that includes archived cards would
 * be the "share one constant across two columns" failure one field up.
 *
 * ---------------------------------------------------------------------------
 * ⚠⚠ `archivedCardSchema.archivedAt` IS THE CARD'S **OWN** `archived_at`,
 * VERBATIM AND NULLABLE — IT IS NOT THE `COALESCE` THE LISTING ORDERS BY, AND
 * A CARD IN THIS ARRAY CAN LEGITIMATELY CARRY `archived: false`
 * ---------------------------------------------------------------------------
 * `GET /boards/:id/archive?type=cards` includes a card when **either**
 * `cards.archived_at IS NOT NULL` **or** its parent list's is —
 * `FR-EXP-010`'s explicit edge case, that a card hidden by an archived list
 * must remain individually findable under Archived items. The listing orders
 * by `COALESCE(cards.archived_at, lists.archived_at) DESC, cards.id DESC` so
 * that a card visible only through its parent still has a position on that
 * axis at all — but **the `COALESCE` is an ordering device, computed at read
 * time, and is not what `archivedAt` below carries.** `archivedAt` is
 * `cards.archived_at IS NOT NULL` **read straight**, exactly as `cardSchema`
 * carries every other column: null on a card the parent list alone hides,
 * non-null on a card somebody actually archived. `cardListRefSchema.archived`
 * on the same row is what tells the two populations apart — "project the
 * fact, filter nothing", `./card-detail`'s rule for the identical
 * `list.archived` field, applied here rather than re-argued. ⚠ **A route
 * agent who wires the `COALESCE` value into this field instead produces a
 * response that still parses** — `archivedAt` is `z.string().nullable()`
 * either way, and nothing red anywhere says the wrong value shipped. The
 * tell, if this is ever miswired, is that `archivedAt` stops being `null` on
 * any row where `list.archived` is `true`.
 *
 * ⚠ **The direct consequence for a consumer of this array: `cardSchema`'s own
 * inherited `archived` boolean can read `false` on a row in this listing.** A
 * card here that got in through the second arm of the `OR` — hidden by its
 * archived parent, never itself archived — carries `archived: false`,
 * `archivedAt: null`, `list.archived: true` and `restorableInPlace: false`
 * (its list is not unarchived, D1). A consumer that filters this array on
 * `archived === true`, expecting every row here to have that flag set, drops
 * exactly the population `FR-EXP-010`'s edge case exists to keep findable —
 * with a 200, a valid body and nothing anywhere that says so. The four fields
 * together, not any one of them alone, are what tells a client which of the
 * two populations a row belongs to.
 *
 * `archivedListSchema.archivedAt`, by contrast, is **never** `null`: the
 * `type=lists` half of the listing's `WHERE` is `lists.archived_at IS NOT
 * NULL` unconditionally — there is no second arm, because a list has no
 * parent for an *its* container to hide it inside — so every row on that page
 * has archived itself. The two fields share a name and a column-family and
 * differ in nullability for that reason alone; neither schema's docstring
 * should be read as a template for the other.
 *
 * ---------------------------------------------------------------------------
 * ⚠⚠ THE CURSOR CODEC IS `./comment`'s PATTERN, COPIED A THIRD TIME ON
 * PURPOSE — AND THE THIRD COPY IS WHERE A NEW PROBLEM SHOWS UP THAT THE
 * FIRST TWO NEVER HAD
 * ---------------------------------------------------------------------------
 * Hand-rolled base64url (`btoa`/`atob` do not typecheck under this
 * workspace's `lib: ["ES2023"]`; `Buffer` breaks Vite's bundling of
 * `apps/web`), and `decodeArchiveCursor` throws `ApiError('validation_failed')`
 * rather than a plain `Error`, on `decodeCommentCursor`'s exact argument:
 * base64 decoding does not fail on garbage, so the shape check *after* the
 * decode is the whole of the validation, and throwing the shape the error
 * handler already understands is what turns a malformed cursor into a
 * structural 422 instead of a matter of handler discipline.
 *
 * `./notification`'s header names this as the second of two callers and
 * predicts a third will **extract** the codec into a shared module — "the
 * block that wants a third keyset stream extracts it then." **This is that
 * third caller, and it does not extract, on the two sentences that already
 * settle the question rather than a fresh one:** `./comment`'s codec doc says
 * *"The shape is the reusable thing, not the function"*, and
 * `./notification`'s says of the very helpers the extraction would move,
 * *"Copied rather than imported, and the duplication is the point … a shared
 * codec would couple two tables' sort keys, and the day one changes the
 * other changes silently."* Both arguments hold unchanged for a third table:
 * this file adopts the **contract** — `?cursor` + `?limit` +
 * `nextCursor: string | null`, a 422 on anything malformed — which is exactly
 * what the "rule of three" note asked a third caller to do, and copies the
 * twenty-line base64url helpers and the measured `POSTGRES_TIMESTAMPTZ`
 * pattern (below) for the third time rather than importing them, because
 * `notification.ts`'s own helpers are module-private *by design* and
 * `comment.ts`'s codec is a shipped, tested file this stage must not disturb
 * for a third caller's convenience. Extracting now would also touch two
 * files this stage does not own while other stages of this same block are
 * landing concurrently — the risk the duplication is deliberately chosen to
 * avoid, not merely tolerate.
 *
 * ⚠⚠ **AND THE THIRD COPY HAS A PROBLEM THE FIRST TWO NEVER FACED: ONE CODEC,
 * TWO STREAMS.** `./comment` and `./notification` each page exactly one
 * table. `GET /boards/:id/archive` pages **two** — `cards` and `lists`,
 * selected by `?type=` and returned through `archivePageSchema`'s
 * discriminated union — and a cursor minted on one page is a syntactically
 * perfect cursor on the other: same base64url alphabet, same
 * `(sortKey, id)` shape, an `id` that is a ULID either way. A client that
 * switches `?type=cards` to `?type=lists` mid-session and forwards the
 * `cards` page's `nextCursor` unmodified would not get a 422 — it would get a
 * `WHERE (lists.archived_at, lists.id) < (<a card's sort key>, <a card's
 * id>)`, which **executes**, returns a syntactically valid page, and is
 * silently the wrong page. That is precisely the failure class this
 * package's docstrings exist to name before it is discovered in production:
 * nothing red, no status code, no field that says so. The cursor payload
 * below therefore carries `type` as its **third** component, and
 * `decodeArchiveCursor` takes the caller's own `?type=` as a required second
 * argument and refuses — the same 422 a malformed cursor gets — the moment
 * the two disagree, so the check is made impossible to forget rather than
 * left for the route to remember. Neither `./comment`'s nor `./notification`'s
 * codec had to solve this, because neither had a sibling stream to be
 * confused with.
 *
 * ---------------------------------------------------------------------------
 * ⚠ NO `SEARCH_QUERY_MIN_LENGTH`, AND NO MINIMUM AT ALL — THIS IS THE KIND OF
 * REUSE THAT LOOKS LIKE A VIRTUE AND ISN'T
 * ---------------------------------------------------------------------------
 * `./search`'s `SEARCH_QUERY_MIN_LENGTH = 3` is a measured property of one
 * specific structure: `0019_search_trgm_indexes.sql`'s **global** trigram
 * index, where a two-character pattern contains no complete trigram and the
 * planner falls back to a sequential scan over every card and board title in
 * the product. Importing it here would be exactly the "never share one
 * constant across two columns" failure `./card`'s header forbids, applied to
 * a *query bound* rather than a column width — the two constants would
 * happen to agree today and diverge the day either measurement is redone,
 * and a reader would have no way to tell which of the two questions the
 * shared number was still answering. `q` here is scoped to one board by an
 * **equality** on `cards.board_id` / `lists.board_id` before the `ILIKE` ever
 * runs — `idx_cards_board_id_archived_at` and
 * `idx_lists_board_id_archived_at_position` serve the scoping conjunct,
 * `EXPLAIN (ANALYZE)`'d in the block contract at 10 buffers and 0.371 ms with
 * no sequential scan anywhere — so there is no degenerate case for a minimum
 * length to defend against: the worst a one-character `q` can do on this
 * route is `ILIKE` over a handful of rows already selected by the equality,
 * which is cheap regardless of pattern length. `q` is therefore
 * `.optional()` with no `.min()` beyond "non-empty after trimming" — see the
 * schema for why that floor is not the same claim as `OQ-7`'s — and no
 * `.max()` either: a maximum would exist only to give an early, cheap 422 to
 * a caller who could not possibly match, which is `./search`'s argument for
 * `SEARCH_QUERY_MAX_LENGTH`, and it does not transfer, because this route's
 * `WHERE` never reaches the trigram index or a sequential scan to be cheap
 * *instead of*. Node's 16 KB header budget refuses an absurd querystring with
 * a **431** long before this route is reached regardless, `./search`'s own
 * backstop, unclaimed here as anything more than that.
 *
 * ---------------------------------------------------------------------------
 * INPUTS ARE `z.strictObject`; VIEWS ARE `z.object`
 * ---------------------------------------------------------------------------
 * `archiveQuerySchema` and `cardRestoreBodySchema` refuse an unknown key with
 * a 422 — `./search`'s and `./board-filter`'s argument, that a parameter this
 * block does not implement (`?archivedBy=`, a bulk `cardIds` on the restore
 * body) must not silently be ignored and answered as though it had been
 * honoured. The three view schemas **strip** an unknown key instead, which is
 * `z.object`'s ordinary behaviour and the trap `./notification`'s header
 * records: `safeParse` on an object carrying an extra field **succeeds**, so
 * an assertion that a field is absent from one of these must go through
 * `Object.keys(schema.shape)` rather than a parse that "should" fail and
 * quietly doesn't.
 *
 * ---------------------------------------------------------------------------
 * VIEWS EXTEND `cardSchema` / `listSchema` ON COPIES; NEITHER BASE IS WIDENED
 * ---------------------------------------------------------------------------
 * `cardSchema` serialises B12's four routes and every element of
 * `boardContentsSchema.cards`; `listSchema` serialises all of B11's routes.
 * Widening either for three fields this listing alone needs would put an
 * `archivedAt`/`restorableInPlace` pair on a board face and a card-move
 * response that never asked for either. `archivedCardSchema` and
 * `archivedListSchema` below extend on copies, restating not one field of
 * either base, on `./search`'s and `./cross-board-move`'s precedent.
 */

// ---------------------------------------------------------------------------
// Pagination bounds. Request-shape constants, not column widths — `./comment`'s
// `COMMENT_PAGE_SIZE_*` precedent, not `./card`'s column-width rule.
// ---------------------------------------------------------------------------

/** The page size when `?limit=` is omitted. */
export const ARCHIVE_PAGE_SIZE_DEFAULT = 50;

/**
 * The largest page a caller may ask for.
 *
 * Matches `COMMENT_PAGE_SIZE_MAX` and `NOTIFICATION_PAGE_SIZE_MAX`'s value by
 * coincidence, not by a shared constant — `./card`'s "never share one across
 * two columns" rule applied to a request bound rather than a column, on
 * `./search`'s `SEARCH_QUERY_MAX_LENGTH` precedent (which makes the same call
 * against `CARD_TITLE_MAX_LENGTH` for the identical reason).
 */
export const ARCHIVE_PAGE_SIZE_MAX = 100;

// ---------------------------------------------------------------------------
// The two archive sub-types, and the discriminator every shape below is built
// around.
// ---------------------------------------------------------------------------

export const ARCHIVE_TYPES = ['cards', 'lists'] as const;

export const archiveTypeSchema = z.enum(ARCHIVE_TYPES);

export type ArchiveType = z.infer<typeof archiveTypeSchema>;

// ---------------------------------------------------------------------------
// The cursor. See the header for why this is a third, deliberately
// un-extracted copy of `./comment`'s codec, and for why its payload carries a
// third component `./comment`'s and `./notification`'s never needed.
// ---------------------------------------------------------------------------

/**
 * An opaque paging cursor. Bounded, not structured — a client reads it from
 * `nextCursor` on either page shape and hands it back verbatim.
 *
 * 128 is comfortably above what `encodeArchiveCursor` produces. The raw string
 * is `` `${type}|${sortKey}|${id}` ``: `type` is 5 bytes either way ('cards'
 * or 'lists'), a Postgres timestamp is at most 26, a ULID is exactly 26, and
 * two `|` separators bring the total to 59 bytes — 80 base64url characters —
 * so 128 leaves the same comfortable margin `./notification`'s identical bound
 * does for a two-component cursor.
 *
 * ⚠ **Bounded here and *decoded* separately.** This schema only says "a
 * plausible cursor-shaped string"; `decodeArchiveCursor` is what refuses a
 * malformed one, and it must be called — base64 decoding does not fail on
 * garbage.
 */
export const archiveCursorSchema = z.string().min(1).max(128);

/**
 * How Postgres renders a `timestamptz`, permissively — copied verbatim from
 * `./notification`'s `POSTGRES_TIMESTAMPTZ`, itself the fix for a hole
 * `./comment`'s original codec left open (see that file's header for the
 * measured `'notatime'` failure this pattern exists to close).
 *
 * ⚠ **Module-private in `./notification`, so this is a THIRD copy, not a
 * second.** The header's "duplication is the point" argument covers this
 * constant as much as the base64 helpers below it: a shared regex would
 * couple this file's cursor to a table it does not page.
 *
 * Measured against the live database in `./notification`'s own header,
 * reused rather than re-measured because the fact it states —
 * `timestamptz(3)::text`'s rendering, and that the fractional part is
 * omitted when it is zero — is a property of Postgres, not of any one
 * column.
 */
const POSTGRES_TIMESTAMPTZ =
  /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}:\d{2}(\.\d{1,6})?([+-]\d{2}(:\d{2}(:\d{2})?)?|Z)?$/;

/**
 * The decoded cursor payload, before the caller's own `?type=` is checked
 * against it — see `decodeArchiveCursor`.
 *
 * Module-private, on `commentCursorPayloadSchema`'s and
 * `notificationCursorPayloadSchema`'s precedent: a caller receives the
 * decoded shape from `decodeArchiveCursor` and never constructs one by hand.
 *
 * ⚠ **`sortKey`, not `archivedAt` or `createdAt`.** This is deliberately NOT
 * the same name as `archivedCardSchema.archivedAt` — see the header's warning
 * about the `COALESCE`. The value this field carries on a `cards` cursor is
 * `COALESCE(cards.archived_at, lists.archived_at)`, which is **never**
 * null for a row that appears in the listing at all, whereas
 * `archivedCardSchema.archivedAt` is `cards.archived_at` alone and **can**
 * be null. Naming both `archivedAt` would invite a reader — or a route
 * agent — to treat them as the same fact; they are the same *kind* of value
 * (a `timestamptz` rendered as text) and not the same value.
 */
const archiveCursorPayloadSchema = z.strictObject({
  type: archiveTypeSchema,
  sortKey: z.string().min(1).max(64).regex(POSTGRES_TIMESTAMPTZ),
  id: ulidSchema,
});

/**
 * The base64url alphabet, unpadded — copied a third time from `./comment`,
 * for the reason the header gives at length. Do not export it, and do not
 * import `./comment`'s or `./notification`'s: three module-private copies is
 * the design, not an oversight.
 */
const BASE64URL_ALPHABET =
  'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';

/** Latin-1 (here: ASCII) in, unpadded base64url out. */
function toBase64Url(input: string): string {
  let out = '';
  for (let i = 0; i < input.length; i += 3) {
    const b0 = input.charCodeAt(i);
    const b1 = input.charCodeAt(i + 1);
    const b2 = input.charCodeAt(i + 2);
    const has1 = !Number.isNaN(b1);
    const has2 = !Number.isNaN(b2);
    const triple = (b0 << 16) | ((has1 ? b1 : 0) << 8) | (has2 ? b2 : 0);
    out += BASE64URL_ALPHABET.charAt((triple >>> 18) & 63);
    out += BASE64URL_ALPHABET.charAt((triple >>> 12) & 63);
    if (has1) out += BASE64URL_ALPHABET.charAt((triple >>> 6) & 63);
    if (has2) out += BASE64URL_ALPHABET.charAt(triple & 63);
  }
  return out;
}

/**
 * Unpadded base64url in, latin-1 out. Throws on a character outside the
 * alphabet and on a length no base64 string can have (`length % 4 === 1`).
 *
 * ⚠ It does **not** throw on every string that merely fails to mean anything
 * — `'abc'` decodes to two bytes of noise quite happily — which is precisely
 * why `decodeArchiveCursor` validates the *shape* afterwards rather than
 * treating a successful decode as proof of anything.
 */
function fromBase64Url(input: string): string {
  if (input.length % 4 === 1) throw new Error('not base64url');
  let out = '';
  let buffer = 0;
  let bits = 0;
  for (const character of input) {
    const value = BASE64URL_ALPHABET.indexOf(character);
    if (value < 0) throw new Error('not base64url');
    // Masked to sixteen bits deliberately — `./comment`'s note: only the low
    // twelve are ever read back, and an unmasked accumulator would run past
    // thirty-two bits, where JavaScript's bitwise operators truncate.
    buffer = ((buffer << 6) | value) & 0xffff;
    bits += 6;
    if (bits >= 8) {
      bits -= 8;
      out += String.fromCharCode((buffer >>> bits) & 0xff);
    }
  }
  return out;
}

/**
 * Turn the last row on a page into the cursor that fetches the next one.
 *
 * ⚠ **VALUE-CARRYING and self-contained — base64url of
 * `` `${type}|${sortKey}|${id}` `` — and emphatically NOT `?before=<id>`.**
 * `./comment`'s two mechanical reasons both apply: a hard-deleted card
 * (`FR-EXP-011`) would dangle a row-naming cursor, and this function takes
 * **no database handle**, which is the property, not an accident.
 *
 * ⚠ **`sortKey` is whatever the caller's own query ordered by, not
 * necessarily `cards.archived_at` or `lists.archived_at` read directly** —
 * for a `cards` page it is the `COALESCE` the route's `ORDER BY` uses, so
 * that a card visible only through its archived parent list still encodes a
 * position to resume from. See `archiveCursorPayloadSchema.sortKey`'s note.
 *
 * ⚠ **Encode from the last row KEPT, never from the extra `limit + 1`th row**
 * fetched to discover whether another page exists — `./comment`'s and
 * `./notification`'s identical warning; from the extra row, every page
 * boundary silently skips exactly one item.
 */
export function encodeArchiveCursor(row: {
  type: ArchiveType;
  sortKey: string;
  id: string;
}): string {
  const raw = `${row.type}|${row.sortKey}|${row.id}`;
  // Guard the one assumption the codec makes. All three components are ASCII
  // by construction (an enum member, a Postgres timestamp and a Crockford
  // base32 ULID), so this can only fire if a caller invents a row — in which
  // case failing loudly here beats emitting a cursor that decodes to
  // mojibake.
  for (let i = 0; i < raw.length; i += 1) {
    if (raw.charCodeAt(i) > 0xff) {
      throw new Error('archive cursor components must be latin-1');
    }
  }
  return toBase64Url(raw);
}

/**
 * Turn a cursor back into the `(sortKey, id)` pair it names, refusing it if
 * it was minted on the **other** archive sub-type.
 *
 * ⚠ **A MALFORMED CURSOR IS A 422, NEVER "START FROM THE TOP"** —
 * `./comment`'s and `./notification`'s argument verbatim: a silent fallback
 * to page one turns "Load more" into an infinite loop with a 200 and a
 * well-formed body, so nothing anywhere fails. That is why this throws
 * `ApiError('validation_failed')` rather than a plain `Error` a route must
 * remember to catch.
 *
 * ⚠ **`expectedType` is REQUIRED, not a convenience overload, and it is what
 * this function has that its two predecessors do not need.** See the
 * header's "one codec, two streams" warning: a cursor minted on a `cards`
 * page is a syntactically perfect cursor on a `lists` page, and without this
 * check the route would build a `WHERE` comparing a card's sort key and id
 * against `lists.archived_at` / `lists.id` — which **executes**, returns a
 * page, and is silently the wrong page. Passing the caller's own `?type=`
 * here makes that check impossible for a route to forget, rather than
 * documenting that the route must remember to compare `decoded.type` against
 * `query.type` itself before using the rest of the payload.
 *
 * ⚠ **The shape check after the decode is the whole defence beyond that**,
 * because base64 decoding does not fail on garbage — `./comment`'s and
 * `./notification`'s identical note. All of these are refusals: a character
 * outside the alphabet, an impossible length, a decoded string that does not
 * split into **exactly three** parts on `|`, a first part that is not
 * `'cards'` or `'lists'`, a first part that does not match `expectedType`, a
 * second part that does not look like a `timestamptz`, and a third part that
 * is not a ULID.
 *
 * `|` is safe as the separator because none of the three components can
 * contain one — `archiveTypeSchema`'s two members are plain lowercase words,
 * a `timestamptz` renders as digits, hyphens, colons, a dot, a space and a
 * sign, and Crockford base32 is `[0-9A-HJKMNP-TV-Z]`.
 */
export function decodeArchiveCursor(
  cursor: string,
  expectedType: ArchiveType,
): { sortKey: string; id: string } {
  const invalid = (): never => {
    throw new ApiError('validation_failed', 'Malformed cursor', {
      cursor: ['must be a cursor returned by a previous page of this listing'],
    });
  };

  let raw: string;
  try {
    raw = fromBase64Url(cursor);
  } catch {
    return invalid();
  }

  const parts = raw.split('|');
  if (parts.length !== 3) return invalid();

  const parsed = archiveCursorPayloadSchema.safeParse({
    type: parts[0],
    sortKey: parts[1],
    id: parts[2],
  });
  if (!parsed.success) return invalid();
  if (parsed.data.type !== expectedType) return invalid();

  return { sortKey: parsed.data.sortKey, id: parsed.data.id };
}

// ---------------------------------------------------------------------------
// Input — what `GET /boards/:id/archive` ACCEPTS.
// ---------------------------------------------------------------------------

/**
 * The querystring of `GET /boards/:id/archive` — `FR-EXP-010`, `FR-BOARD-029`.
 *
 * `z.strictObject`, on `./search`'s and `./board-filter`'s argument: a
 * parameter this block does not implement (`?archivedBy=`, a date range) must
 * be a 422 rather than a silently ignored key answered with a correct-looking
 * unfiltered page.
 */
export const archiveQuerySchema = z.strictObject({
  /**
   * Which half of the archive to list. `'cards'` by default, because
   * `FR-EXP-010`'s behaviour text leads with "archived cards by default with
   * a switch to view archived lists" — this parameter **is** that switch.
   */
  type: archiveTypeSchema
    .default('cards')
    .describe(
      'Which archive to list: "cards" (default) or "lists". Determines the ' +
        'shape of nextCursor and every element of items, per archivePageSchema’s ' +
        'discriminated union.',
    ),
  /**
   * Filter by title, case-insensitive substring. Optional — omit it entirely
   * for the unfiltered listing, `FR-EXP-010`'s ordinary path.
   *
   * ⚠ **No minimum length, deliberately — see the header's argument against
   * importing `SEARCH_QUERY_MIN_LENGTH`.** `.min(1)` below is a
   * well-formedness floor ("a present `q` must not be empty after trimming"),
   * not a performance floor: it exists so `?q=` (present, empty) is refused
   * rather than silently treated as "no filter", which a client wanting no
   * filter should express by omitting the key. It is not `OQ-7`'s measured
   * three-character floor and must not be read as a smaller version of it.
   *
   * `.trim()` runs before `.min()`, `./search`'s load-bearing ordering: under
   * this workspace's Zod, `z.string().min(1).trim()` would accept `' '` and
   * hand the route a request to filter on a single space. The parsed value
   * therefore arrives at the route **already trimmed** — the handler must not
   * trim it again, `./search`'s identical instruction to its own route.
   */
  q: z
    .string()
    .trim()
    .min(1)
    .optional()
    .describe(
      'Filter by title, case-insensitive substring, escaped and wrapped by the ' +
        'route with escapeLikePattern from ./search before being matched against ' +
        'cards.title or lists.title (whichever "type" selects). Omit for the ' +
        'unfiltered listing. No minimum length: this filter is scoped to one ' +
        'board by an equality on board_id and never degenerates to a sequential ' +
        'scan, unlike global search.',
    ),
  /** 1 to `ARCHIVE_PAGE_SIZE_MAX`, default `ARCHIVE_PAGE_SIZE_DEFAULT`. */
  limit: z.coerce
    .number()
    .int()
    .min(1)
    .max(ARCHIVE_PAGE_SIZE_MAX)
    .default(ARCHIVE_PAGE_SIZE_DEFAULT)
    .describe(
      `How many rows to return, 1 to ${ARCHIVE_PAGE_SIZE_MAX}, default ` +
        `${ARCHIVE_PAGE_SIZE_DEFAULT}. Coerced from the query string, so the ` +
        'bounds apply to the number rather than to the string’s length.',
    ),
  cursor: archiveCursorSchema
    .optional()
    .describe(
      'An opaque cursor from a previous response’s nextCursor. Omit it for the ' +
        'first (most-recently-archived) page. A cursor minted while "type" was ' +
        'set to the OTHER value is a 422, not a silently wrong page — see ' +
        'decodeArchiveCursor.',
    ),
});

export type ArchiveQuery = z.infer<typeof archiveQuerySchema>;

/**
 * `POST /cards/:id/restore` — `FR-EXP-010`, `FR-CARD-028`, `FR-TMPL-031`.
 *
 * ⚠ **`listId` is `.optional()`, not `.nullable().default(null)`** — unlike
 * `cardNeighbourSchema` and its relatives in `./card` and `./list`, where an
 * omitted key and an explicit `null` are deliberately the same request. There
 * is no gesture here that means "explicitly no destination": omitting the
 * key is Branch A/B (restore in place, or the `409` naming that a
 * destination is required — D1/`OQ-3`), and *supplying* one is Branch C. A
 * client that sent `listId: null` would be asking for something between
 * those two that does not exist, and `ulidSchema` with no `.nullable()`
 * refuses it as the type error it is, rather than silently folding it into
 * Branch A/B.
 *
 * A `listId` naming a list on **another board**, a deleted list, or an
 * archived one is not refused here — a Zod schema cannot express "live,
 * unarchived and on this card's board" — and is the route's job. ⚠
 * **Corrected in place: all three of those fold into the identical `404` a
 * nonexistent list gets**, B12's construction — "another board", "deleted"
 * and "archived" are not three refusals, they are one refusal reached three
 * ways, because a Zod schema cannot distinguish "wrong board" from "does not
 * exist" any more than the route may. The `422` this file used to attach to
 * "archived or deleted" belongs to a *different* case entirely, one this
 * schema cannot see at all: the card's own list already being live and
 * unarchived (`restorableInPlace` true) and `listId` naming some other list
 * anyway. That is a request for a *move*, which `POST /cards/:id/move`
 * handles and this route refuses — see the block contract's Branch C rules,
 * and `apps/server/src/routes/card-archive.ts`'s own header for the two
 * refusals argued side by side.
 */
export const cardRestoreBodySchema = z.strictObject({
  listId: ulidSchema
    .optional()
    .describe(
      'The destination list, ON THE SAME BOARD, to restore the card into. ' +
        'Omit it to restore in place — legal only when restorableInPlace is ' +
        'true for this card; otherwise the request is refused with a 409 ' +
        'naming that a destination is required (FR-EXP-010 AC3). Supplying the ' +
        'card’s own CURRENT list is accepted and behaves identically to ' +
        'omitting the field.',
    ),
});

export type CardRestoreBody = z.infer<typeof cardRestoreBodySchema>;

// ---------------------------------------------------------------------------
// Views — what `GET /boards/:id/archive` RETURNS. Both extend a shipped view
// on a copy; neither `cardSchema` nor `listSchema` is widened. See the
// header.
// ---------------------------------------------------------------------------

/**
 * One row of the `cards` archive — `cardSchema` plus three fields, on a
 * **copy**.
 *
 * ⚠ **A row here can carry `archived: false`.** Read the header's warning in
 * full before consuming this array by filtering on `cardSchema.archived`
 * alone; the four fields below, together, are what distinguishes a card
 * archived in its own right from one merely hidden by its archived parent
 * list.
 */
export const archivedCardSchema = cardSchema.extend({
  /**
   * `cards.archived_at`, read straight — nullable, and **not** the
   * `COALESCE` this listing orders by. See the header's warning at length:
   * `null` here means this card's presence in the listing is entirely owed
   * to its parent list, per `list.archived` on this same row.
   */
  archivedAt: z.string().nullable(),
  /**
   * The list this card is in — id, title and archived flag. `cardListRefSchema`
   * from `./card-detail`, reused rather than re-declared, on `./search`'s
   * precedent for the identical shape.
   *
   * ⚠ **This is the field that tells apart the two populations `archivedAt`
   * alone cannot.** `list.archived: true` beside `archivedAt: null` is a card
   * present only because its list is archived; `list.archived: false` beside
   * a non-null `archivedAt` is a card archived in its own right; both can be
   * `true` when a member archived a card and its list separately, in either
   * order — `FR-EXP-010`'s edge case does not forbid that combination, and
   * neither should a client's rendering of it.
   */
  list: cardListRefSchema,
  /**
   * `true` exactly when this card's own list is both live
   * (`lists.deleted_at IS NULL`) and unarchived (`lists.archived_at IS
   * NULL`) — the wire form of D1/`OQ-3`. See the header's argument for why
   * this is served rather than left for a client to infer from `list`.
   *
   * ⚠ **Within THIS listing the deletedness half is always true by
   * construction** — `GET /boards/:id/archive`'s own `WHERE` already
   * excludes `lists.deleted_at IS NOT NULL` for every row it returns (a card
   * whose list has been hard-deleted is itself soft-deleted by the same
   * cascade and is absent from every read path, `isLiveCard()`) — so within
   * this array the field is observably `!list.archived`. **It is spelled out
   * as the full two-part predicate anyway, because `POST
   * /cards/:id/restore` computes the identical fact server-side against a
   * card that may be acted on from a stale client state**, where the
   * destination list could since have been hard-deleted between the archive
   * listing being drawn and the restore button being clicked. Do not
   * shortcut a client's own logic to `!list.archived` on the strength of
   * this listing's `WHERE`: the shortcut is correct here today and is not a
   * property of the field.
   */
  restorableInPlace: z.boolean(),
});

export type ArchivedCard = z.infer<typeof archivedCardSchema>;

/**
 * One row of the `lists` archive — `listSchema` plus one field, on a
 * **copy**.
 *
 * ⚠ **`cardCount` is NOT redeclared here — it is inherited from `listSchema`
 * unchanged, and that is deliberate.** See the header's argument in full:
 * `listSchema.cardCount` already counts exactly what `FR-EXP-011` AC2 needs
 * ("open and archived alike", `deleted_at IS NULL`), because B11 wrote it for
 * `FR-BOARD-016`'s identical confirmation. Extending with `{ archivedAt }`
 * alone is what makes the two confirmations provably read one value.
 */
export const archivedListSchema = listSchema.extend({
  /**
   * `lists.archived_at`, read straight.
   *
   * ⚠ **Never `null` on this shape**, unlike `archivedCardSchema.archivedAt`.
   * `GET /boards/:id/archive?type=lists`'s `WHERE` is `lists.archived_at IS
   * NOT NULL` unconditionally — there is no second arm, because a list has
   * no parent container for `FR-EXP-010`'s "hidden by its parent" edge case
   * to apply to one level up. Every row on this page archived itself.
   */
  archivedAt: z.string(),
});

export type ArchivedList = z.infer<typeof archivedListSchema>;

/**
 * `GET /boards/:id/archive?type=cards` — the `cards` page.
 *
 * ⚠ **`nextCursor: string | null`, never `.optional()`** — `./comment`'s and
 * `./notification`'s identical shape: absence and emptiness are not the same
 * thing, and only one of them can happen. `null` means this is the last
 * page.
 */
export const archivedCardPageSchema = z.object({
  type: z.literal('cards'),
  /**
   * Most-recently-archived first —
   * `COALESCE(cards.archived_at, lists.archived_at) DESC, cards.id DESC`,
   * `FR-EXP-010`'s "ordered most-recently-archived first". The `id`
   * tie-break is not decoration: two cards archived in the same
   * transaction-timestamp millisecond is a genuine tie, `./comment`'s and
   * `./search`'s identical reasoning for their own tie-breaks.
   */
  items: z.array(archivedCardSchema),
  nextCursor: archiveCursorSchema.nullable(),
});

export type ArchivedCardPage = z.infer<typeof archivedCardPageSchema>;

/**
 * `GET /boards/:id/archive?type=lists` — the `lists` page.
 *
 * Same paging contract as the `cards` page, ordered
 * `lists.archived_at DESC, lists.id DESC`.
 */
export const archivedListPageSchema = z.object({
  type: z.literal('lists'),
  items: z.array(archivedListSchema),
  nextCursor: archiveCursorSchema.nullable(),
});

export type ArchivedListPage = z.infer<typeof archivedListPageSchema>;

/**
 * What `GET /boards/:id/archive` answers with — a discriminated union on
 * `type`, so a client's `switch` on the response narrows `items`' element
 * type without a cast.
 *
 * ⚠ **There is no envelope carrying both arrays at once.** `?type=` selects
 * exactly one; a client wanting both makes two requests, exactly as
 * `?type=open`/`?type=archived` on `GET /boards/:id/lists` (B11) does not
 * answer with both in one call. A combined shape would also have no single
 * `nextCursor` to page by, since the two populations page independently.
 */
export const archivePageSchema = z.discriminatedUnion('type', [
  archivedCardPageSchema,
  archivedListPageSchema,
]);

export type ArchivePage = z.infer<typeof archivePageSchema>;
